// Mahjong entry point. Boots into the MainMenu (ui-shell MenuRouter) and hands
// the canvas to GameScene only when the user picks Free play. Boot order mirrors
// sudoku: settings → i18n → canvas + layout + scene + router → MainMenu.
// Construction detail lives in Bootstrap/GameScene/MenuContextImpl; this file
// stays glue: it owns the menu↔game scene toggle (canvas visibility + scene
// mount/unmount) so the rAF loop and input listeners never leak.

import '@bn-games/ui-shell/styles.css';
import { MenuRouter, setupMenuScale } from '@bn-games/ui-shell';
import { initDiscordActivity, isDiscordActivity } from '@bn-games/discord-activity';
import { fetchMe } from '@bn-games/auth-client';
import { readRoomHash } from '@bn-games/room-client';
import { LayoutManager } from './layout/LayoutManager.js';
import { SettingsManager } from './settings/SettingsManager.js';
import { GameScene } from './scenes/GameScene.js';
import { LessonScene } from './scenes/LessonScene.js';
import { buildMenuContext } from './scenes/MenuContextImpl.js';
import { OnlineMenuHost } from './scenes/OnlineMenuHost.js';
import { PauseHost } from './scenes/PauseHost.js';
import { buildSettingsRows } from './ui/settingsRows.js';
import { createMainMenuScreen } from './ui/screens/MainMenu.js';
import { mountSocialDock } from './ui/socialDock.js';
import { resolveLobbyServerUrl } from './scenes/onlineHostConfig.js';
import { createPlayMenuScreen } from './ui/screens/PlayMenu.js';
import { createOnlineCreateScreen } from './ui/screens/OnlineCreateScreen.js';
import { createOnlineLobbyScreen } from './ui/screens/OnlineLobbyScreen.js';
import { createOnlineJoinScreen } from './ui/screens/OnlineJoinScreen.js';
import { createLessonListScreen } from './ui/screens/LessonList.js';
import { createRulesScreen } from './ui/screens/Rules.js';
import { createSettingsScreen } from './ui/screens/Settings.js';
import { createAboutScreen } from './ui/screens/About.js';
import { initI18n, setLocale } from './i18n/I18n.js';
import { preloadTileSprites } from './render/tileSprites.js';
import { THEMES } from './settings/themes.js';
import type { LayoutConfig } from './layout/types.js';
import type { Scene } from './ui/types.js';
import type { Settings } from './settings/types.js';

// ui-shell's stylesheet keys its palette selectors off the base theme's
// `name` (e.g. `body[data-theme="monte"]`), not our settings key
// ('greenFelt') — those differ because mahjong reuses the Monte Carlo base
// palette under its own theme id.
function applyTheme(theme: Settings['theme']): void {
  document.body.setAttribute('data-theme', THEMES[theme].name);
}

function getCanvas(): HTMLCanvasElement {
  const el = document.getElementById('game-canvas');
  if (!(el instanceof HTMLCanvasElement)) throw new Error('#game-canvas not found');
  return el;
}

function getMenuRoot(): HTMLElement {
  const el = document.getElementById('menu-root');
  if (el === null) throw new Error('#menu-root not found');
  return el;
}

// Match the canvas backing store + CSS size to the layout snapshot. Renderers
// work in CSS pixels; the DPR-scaled transform maps to physical pixels.
function applyCanvasLayout(canvas: HTMLCanvasElement, layout: LayoutConfig): void {
  const { width: cssW, height: cssH } = layout.viewport;
  const dpr = layout.devicePixelRatio;
  const bmW = Math.max(1, Math.round(cssW * dpr));
  const bmH = Math.max(1, Math.round(cssH * dpr));
  if (canvas.width !== bmW) canvas.width = bmW;
  if (canvas.height !== bmH) canvas.height = bmH;
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Failed to acquire 2D canvas context');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function setCanvasVisible(canvas: HTMLCanvasElement, visible: boolean): void {
  canvas.style.display = visible ? 'block' : 'none';
  document.body.setAttribute('data-mode', visible ? 'game' : 'menu');
}

async function main(): Promise<void> {
  const settingsManager = new SettingsManager();
  // Both are independent of each other and of the DOM below — load them
  // together so tile art is guaranteed ready before any scene can mount.
  await Promise.all([initI18n(settingsManager.get().language), preloadTileSprites()]);
  setupMenuScale();
  applyTheme(settingsManager.get().theme);

  document.body.style.margin = '0';
  document.body.style.overflow = 'hidden';

  const canvas = getCanvas();
  setCanvasVisible(canvas, false);
  const layoutManager = new LayoutManager();
  applyCanvasLayout(canvas, layoutManager.getConfig());

  const socialDock = mountSocialDock({
    serverUrl: resolveLobbyServerUrl(),
    isGameInProgress: () => currentScene === 'game' && gameScene.isInProgress(),
  });
  const pillBottom = (): number | null =>
    socialDock.pillBottomOn(canvas, layoutManager.getConfig().viewport.height);
  // The canvas gear does what Escape does. `pauseHost` is built further down.
  const onPause = (): void => pauseHost?.toggle();
  const gameScene = new GameScene({
    canvas,
    layoutManager,
    settingsManager,
    onMatchComplete: () => navigateTo('menu'),
    pillBottom,
    onPause,
  });
  const lessonScene = new LessonScene({ canvas, layoutManager, settingsManager, pillBottom, onPause });
  socialDock.onPillChange(() => {
    gameScene.markDirty();
    lessonScene.markDirty();
  });
  layoutManager.onChange((cfg) => {
    applyCanvasLayout(canvas, cfg);
    gameScene.markDirty();
    lessonScene.markDirty();
  });

  const menuRoot = getMenuRoot();

  // Sentinel — `navigateTo` short-circuits same-scene no-ops, so we start on a
  // value no real route uses; the first explicit navigateTo() lands.
  let currentScene: Scene | null = null;
  // Declared up here because the glue below closes over it while it is built
  // further down (it needs the menu context). Optional access rather than a
  // forward `const` reference, so the order can never bite as a TDZ throw.
  let pauseHost: PauseHost | undefined;

  function navigateTo(scene: Scene): void {
    if (scene === currentScene) return;
    const leavingGame = scene !== 'game' && currentScene === 'game';
    currentScene = scene;
    socialDock.showScene(scene);
    // Both scenes share the 'game' canvas value; only the mounted one is live,
    // and unmount is idempotent, so tearing both down on exit leaks nothing.
    if (leavingGame) {
      // Before the scenes go: the pause card outlives them otherwise, and
      // would be left floating over the menu with no table behind it.
      pauseHost?.dismiss();
      gameScene.unmount();
      lessonScene.unmount();
    }
    if (scene === 'game') {
      router.hide();
      setCanvasVisible(canvas, true);
      return;
    }
    setCanvasVisible(canvas, false);
    router.show(scene);
  }

  // Leaving a live table by any route. Dropping the room here rather than in
  // `navigateTo` keeps it to the deliberate exits: a socket must not outlive
  // the table, but nor should a stray navigation tear down a live room.
  //
  // Gated on actually being at one. `disconnect` also clears the saved session,
  // so firing it on a solo or lesson exit would throw away the rejoin token of
  // an online seat the player has dropped out of but still holds.
  function leaveGame(): void {
    if (onlineHost.isAtTable()) onlineHost.disconnect();
    navigateTo('menu');
  }

  function onSettingsChanged(before: Settings, after: Settings): void {
    if (after.language !== before.language) {
      void setLocale(after.language).then(() => {
        router.remountCurrent();
        // The pause card is not the router's, so it repaints itself.
        pauseHost?.refresh();
      });
    }
    if (after.theme !== before.theme) {
      applyTheme(after.theme);
    }
  }

  // Owns the RoomClient and the single menu→table handoff, so the online
  // screens stay DOM-only and never see a socket.
  const onlineHost = new OnlineMenuHost({
    gameScene,
    navigate: (scene) => navigateTo(scene),
    initialRulesetId: settingsManager.get().variant,
    isPlaying: () => currentScene === 'game',
  });

  const ctxMenu = buildMenuContext({
    gameScene,
    lessonScene,
    settingsManager,
    navigate: (scene) => navigateTo(scene),
    onSettingsChanged,
    // `pre-game` is the lessons list route (see the register call below).
    exitToLessons: () => navigateTo('pre-game'),
    online: onlineHost,
  });

  const router = new MenuRouter(menuRoot, ctxMenu);
  // Resolve the signed-in state once at boot. Mahjong has no local history to
  // sync (so no serverSync caller, unlike the other three games), but the
  // Discord bootstrap reaches a table without ever mounting the AuthWidget
  // that otherwise primes the shared profile cache — leaving ui-shell's
  // AuthWidget and invite dock to each fetch it themselves later.
  void fetchMe();
  router.register('menu', createMainMenuScreen);
  router.register('play', createPlayMenuScreen);
  // `pre-game` hosts the lessons list — ui-shell's Scene union has no `lessons`
  // member and is off-limits here, so we reuse this otherwise-unused value.
  router.register('pre-game', createLessonListScreen);
  router.register('rules', createRulesScreen);
  router.register('settings', createSettingsScreen);
  router.register('about', createAboutScreen);
  router.register('online-create', createOnlineCreateScreen);
  router.register('online-join', createOnlineJoinScreen);
  // `online` is the lobby — the room you land in from both creating and
  // joining by code; only "Start now" differs between the two.
  router.register('online', createOnlineLobbyScreen);

  // The way back out of a live hand: Escape opens the pause menu, and quitting
  // from it is what leaves. See `scenes/PauseHost.ts`.
  pauseHost = new PauseHost({
    gameScene,
    settingsRows: () => buildSettingsRows(ctxMenu),
    inGame: () => currentScene === 'game',
    quit: () => leaveGame(),
  });

  navigateTo('menu');

  // Inside Discord the channel's instance room IS the entry point: join it and
  // go straight to the lobby, so the player watches the table fill instead of
  // hunting for an online button that has no room code to offer. A failed
  // handshake leaves the menu where it already is — a broken activity must not
  // dead-end. Outside Discord, an accepted invite link's `#room=CODE` hash
  // (the invite dock's `/mahjong/#room=CODE`) resumes that seat instead — the
  // two entry points stay mutually exclusive, same as the other three games.
  if (isDiscordActivity()) {
    void initDiscordActivity().then((discord) => {
      if (discord === null) return;
      onlineHost.joinDiscordInstance(discord);
      navigateTo('online');
    });
  } else {
    const hashEntry = readRoomHash();
    if (hashEntry) {
      void onlineHost.resumeRoom(hashEntry.roomCode, hashEntry.legacyToken);
    }
  }
}

void main();
